import React, { useState, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  useToast,
  FormErrorMessage,
} from '@chakra-ui/react'
import { FiEdit, FiFolder } from 'react-icons/fi'
import { Folder } from '@/types'
import { useAppTranslation } from '@/contexts/LanguageContext'

interface EditFolderModalProps {
  isOpen: boolean
  onClose: () => void
  folder: Folder
  onUpdate: (updatedFolder: Folder) => void
}

export default function EditFolderModal({
  isOpen,
  onClose,
  folder,
  onUpdate,
}: EditFolderModalProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<{ name?: string; description?: string }>({})
  const toast = useToast()
  const { t } = useAppTranslation(['files', 'common', 'errors'])

  useEffect(() => {
    if (isOpen && folder) {
      setName(folder.name)
      setDescription(folder.description || '')
      setErrors({})
    }
  }, [isOpen, folder])

  const validateForm = () => {
    const newErrors: { name?: string; description?: string } = {}

    if (!name.trim()) {
      newErrors.name = t('validation.folderNameRequired', { ns: 'errors' })
    } else if (name.trim().length < 2) {
      newErrors.name = t('validation.folderNameTooShort', { ns: 'errors' })
    } else if (name.trim().length > 100) {
      newErrors.name = t('validation.folderNameTooLong', { ns: 'errors' })
    }

    if (description && description.length > 500) {
      newErrors.description = t('validation.descriptionTooLong', { ns: 'errors' })
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    setIsLoading(true)
    try {
      // Create the updated folder object for the callback
      const updatedFolder: Folder = {
        ...folder,
        name: name.trim(),
        description: description.trim() || undefined,
      }

      onUpdate(updatedFolder)
      onClose()

      toast({
        title: t('folders.updateFolderSuccess', { ns: 'files' }),
        description: t('folders.updateFolderSuccessDescription', {
          ns: 'files',
          name: updatedFolder.name,
        }),
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    } catch (error: any) {
      console.error('Failed to update folder:', error)
      toast({
        title: t('status.error', { ns: 'common' }),
        description:
          error.response?.data?.message || t('folders.updateFolderError', { ns: 'files' }),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      setName('')
      setDescription('')
      setErrors({})
      onClose()
    }
  }

  const isFormChanged = name !== folder?.name || description !== (folder?.description || '')

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="xl" overflow="hidden">
        <ModalHeader
          background="linear-gradient(135deg, var(--chakra-colors-brand-500), var(--chakra-colors-brand-700))"
          color="white"
          borderTopRadius="xl"
          borderBottom="1px solid"
          borderColor="brand.600"
        >
          <span>{t('folders.editFolder', { ns: 'files' })}</span>
        </ModalHeader>
        <ModalCloseButton color="white" _hover={{ bg: 'whiteAlpha.200' }} />

        <ModalBody pt={6} pb={2}>
          <VStack spacing={5} align="stretch">
            <FormControl isInvalid={!!errors.name}>
              <FormLabel color="gray.700" fontWeight="semibold" mb={2}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FiFolder size="14px" />
                  <span>{t('folders.folderName', { ns: 'files' })}</span>
                </div>
              </FormLabel>
              <Input
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                  if (errors.name) {
                    setErrors((prev) => ({ ...prev, name: undefined }))
                  }
                }}
                placeholder={t('folders.folderNamePlaceholder', { ns: 'files' })}
                size="md"
                borderRadius="lg"
                bg="gray.50"
                border="1px solid"
                borderColor="gray.200"
                _hover={{ borderColor: 'gray.300', bg: 'white' }}
                _focus={{
                  borderColor: 'blue.500',
                  bg: 'white',
                  boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
                }}
              />
              <FormErrorMessage>{errors.name}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.description}>
              <FormLabel color="gray.700" fontWeight="semibold" mb={2}>
                {t('folders.descriptionOptional', { ns: 'files' })}
              </FormLabel>
              <Textarea
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value)
                  if (errors.description) {
                    setErrors((prev) => ({ ...prev, description: undefined }))
                  }
                }}
                placeholder={t('folders.descriptionPlaceholder', { ns: 'files' })}
                resize="vertical"
                minHeight="80px"
                maxHeight="120px"
                borderRadius="lg"
                bg="gray.50"
                border="1px solid"
                borderColor="gray.200"
                _hover={{ borderColor: 'gray.300', bg: 'white' }}
                _focus={{
                  borderColor: 'blue.500',
                  bg: 'white',
                  boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
                }}
              />
              <FormErrorMessage>{errors.description}</FormErrorMessage>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter
          borderTop="1px solid"
          borderColor="gray.200"
          bg="gray.50"
          borderBottomRadius="xl"
        >
          <Button
            variant="ghost"
            mr={3}
            onClick={handleClose}
            isDisabled={isLoading}
            _hover={{ bg: 'gray.100' }}
          >
            {t('buttons.cancel', { ns: 'common' })}
          </Button>
          <Button
            colorScheme="brand"
            onClick={handleSubmit}
            isLoading={isLoading}
            loadingText={t('status.updating', { ns: 'common' })}
            isDisabled={!isFormChanged || !name.trim()}
          >
            {t('folders.updateFolder', { ns: 'files' })}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
